'use es6';

import once from 'hs-lodash/once';
import * as api from 'laboratory-lib/core/api';
import { resolveTreatments, createAsyncResolver } from 'laboratory-lib/core/utils';
import { getOverridesFromSearch, getOverridesFromCache, getCacheKeys, saveOverridesToCache } from 'laboratory-lib/core/overrides';
import { validateCallback } from 'laboratory-lib/core/validators';

function getAsyncNetworkAdapter() {
  var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      apiDomain = _ref.apiDomain,
      ceid = _ref.ceid,
      http = _ref.http,
      quickFetch = _ref.quickFetch,
      quickFetchLabel = _ref.quickFetchLabel,
      schema = _ref.schema,
      timeout = _ref.timeout;

  var getDataFromHttp = function getDataFromHttp() {
    var opts = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    api.getTreatmentsFromHttp({
      apiDomain: apiDomain,
      ceid: ceid,
      data: Object.keys(schema).map(function (key) {
        return {
          key: key,
          identifier: schema[key].identifier
        };
      }),
      http: http,
      onComplete: opts.onComplete,
      onError: opts.onError,
      timeout: timeout
    });
  };

  var httpRetryHandler = function httpRetryHandler() {
    var opts = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    return function (err) {
      // don't retry for timeouts
      var message = err ? err.message || err || '' : '';

      if (message.indexOf('timeout') === -1) {
        getDataFromHttp(opts);
      } else {
        opts.onError(err);
      }
    };
  };

  var getDataFromQuickFetch = function getDataFromQuickFetch() {
    var opts = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    api.getTreatmentsFromQuickFetch({
      onComplete: opts.onComplete,
      onError: httpRetryHandler(opts),
      quickFetch: quickFetch,
      quickFetchLabel: quickFetchLabel,
      timeout: timeout
    });
  };

  if (quickFetchLabel) {
    return getDataFromQuickFetch;
  }

  return getDataFromHttp;
}

function wrapNetworkAdapterWithMeta(_ref2) {
  var asyncStore = _ref2.asyncStore,
      cacheKeys = _ref2.cacheKeys,
      ceid = _ref2.ceid,
      search = _ref2.search,
      schema = _ref2.schema,
      networkAdapter = _ref2.networkAdapter,
      onError = _ref2.onError;
  var overridesFromSearch = getOverridesFromSearch({
    cacheKeys: cacheKeys,
    search: search
  });
  var asyncOverridesFromCache = getOverridesFromCache({
    cacheKeys: cacheKeys,
    asyncStore: asyncStore,
    onError: onError
  });
  var data = {
    overridesFromCache: {},
    overridesFromSearch: overridesFromSearch,
    schema: schema,
    treatments: []
  };
  return function () {
    var opts = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    asyncOverridesFromCache.then(function (overridesFromCache) {
      data.overridesFromCache = overridesFromCache;
      var requiresNetwork = !!ceid || !cacheKeys.length || cacheKeys.length && cacheKeys.some(function (_ref3) {
        var key = _ref3.key;
        return !overridesFromCache[key];
      });

      if (requiresNetwork) {
        networkAdapter({
          onComplete: function onComplete() {
            var treatments = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
            data.treatments = treatments;
            opts.onComplete(data);
          },
          onError: function onError(err) {
            opts.onError(err, data);
          }
        });
      } else {
        opts.onComplete(data);
      }
    });
  };
}

export function createTreatmentsResolver() {
  var _ref4 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      asyncStore = _ref4.asyncStore,
      ceid = _ref4.ceid,
      http = _ref4.http,
      onError = _ref4.onError,
      quickFetch = _ref4.quickFetch,
      quickFetchLabel = _ref4.quickFetchLabel,
      search = _ref4.search,
      schema = _ref4.schema,
      timeout = _ref4.timeout;

  var resolved = false;
  var cacheKeys = getCacheKeys({
    schema: schema
  });
  var asyncNetworkAdapter = getAsyncNetworkAdapter({
    ceid: ceid,
    http: http,
    quickFetch: quickFetch,
    quickFetchLabel: quickFetchLabel,
    schema: schema,
    timeout: timeout
  });
  var getAsyncData = wrapNetworkAdapterWithMeta({
    asyncStore: asyncStore,
    cacheKeys: cacheKeys,
    ceid: ceid,
    search: search,
    schema: schema,
    networkAdapter: asyncNetworkAdapter,
    onError: onError
  });
  var asyncTreatmentsResolver = createAsyncResolver({
    getAsyncData: getAsyncData,
    resolve: once(resolveTreatments),
    onError: onError
  });
  return {
    resolveTreatments: function resolveTreatments(callback) {
      validateCallback(callback, 'treatmentsClient.resolve', 'treatmentsClient.resolve((treatments) => {}');
      asyncTreatmentsResolver(function (treatments) {
        if (!resolved) {
          saveOverridesToCache({
            schema: schema,
            treatments: treatments,
            asyncStore: asyncStore,
            onError: onError
          });
        }

        callback(treatments);
        resolved = true;
      });
    }
  };
}